import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, Copy, LogOut, Plus, KeyRound, Sparkles, BarChart3, CheckCircle2, Circle as CircleIcon, Mail, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const FAMILY_HERO = "https://images.unsplash.com/photo-1633306196260-82ee97728271?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGZhbWlseSUyMGhpa2luZyUyMG91dGRvb3JzfGVufDB8fHx8MTc3OTc4ODkyNXww&ixlib=rb-4.1.0&q=85";

function ProgressTab() {
  const [members, setMembers] = useState(null);
  useEffect(() => {
    api.get("/family/progress").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, []);

  if (!members) return <Card className="card-treasure p-8 text-center text-ink-500">Caricamento...</Card>;
  if (members.length === 0) return <Card className="card-treasure p-8 text-center text-ink-500">Nessun membro</Card>;

  return (
    <div className="space-y-4">
      {members.map((m, idx) => (
        <Card key={m.user_id} className="card-treasure p-5" data-testid={`progress-member-${m.user_id}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center font-display font-extrabold text-ink-900 shadow-tactile">
              {idx + 1}
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-xl text-ink-900">{m.name}</div>
              <div className="text-sm text-ink-500">{m.email}</div>
            </div>
            <div className="text-right">
              <div className="font-display font-extrabold text-2xl text-gold-hover">{m.total_points}</div>
              <div className="text-xs uppercase tracking-widest font-bold text-ink-500">punti</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="p-3 rounded-2xl bg-forest/10 text-center">
              <div className="text-2xl font-display font-extrabold text-forest">{m.completed_count}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">completati</div>
            </div>
            <div className="p-3 rounded-2xl bg-ocean-light text-center">
              <div className="text-2xl font-display font-extrabold text-ocean">{m.in_progress_count}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">in corso</div>
            </div>
            <div className="p-3 rounded-2xl bg-gold-light text-center">
              <div className="text-2xl font-display font-extrabold text-gold-hover">{m.per_route.length}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-ink-500">avventure</div>
            </div>
          </div>

          {m.monthly_points.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 size={16} className="text-ink-500" />
                <span className="text-xs uppercase tracking-widest font-bold text-ink-500">Punti per mese</span>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={m.monthly_points}>
                  <XAxis dataKey="month" stroke="#8D6E63" fontSize={11} />
                  <YAxis stroke="#8D6E63" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#FDF8F0", border: "1px solid #D7CCC8", borderRadius: 12 }}
                    labelStyle={{ color: "#3E2723", fontWeight: 700 }}
                  />
                  <Bar dataKey="points" fill="#F5A623" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {m.per_route.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs uppercase tracking-widest font-bold text-ink-500">Percorsi</span>
              {m.per_route.slice(0, 5).map((r) => (
                <div key={r.route_id || r.route_title} className="flex items-center gap-2 p-2 rounded-xl bg-parchment-dark">
                  {r.status === "completed" ? (
                    <CheckCircle2 size={16} className="text-forest" />
                  ) : (
                    <CircleIcon size={16} className="text-ocean" />
                  )}
                  <span className="flex-1 text-sm font-bold text-ink-800 truncate">{r.route_title}</span>
                  <span className="text-xs font-display font-extrabold text-gold-hover">{r.points_earned} pt</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function InviteTab() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState(null);

  const loadInvites = async () => {
    try {
      const { data } = await api.get("/family/invites");
      setInvites(data);
    } catch {
      setInvites([]);
    }
  };

  useEffect(() => { loadInvites(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/family/invite", { email });
      if (data.mocked) {
        toast.success(`Invito creato per ${email} (modalità test — nessuna email reale)`);
      } else {
        toast.success(`Email di invito inviata a ${email}`);
      }
      setEmail("");
      await loadInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio invito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="card-treasure p-6" data-testid="invite-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-ocean-light flex items-center justify-center">
            <Mail className="text-ocean" size={22}/>
          </div>
          <div>
            <h2 className="font-display font-bold text-xl text-ink-900">Invita via email</h2>
            <p className="text-sm text-ink-500">Mandiamo noi il codice famiglia al destinatario</p>
          </div>
        </div>
        <form onSubmit={sendInvite} className="flex flex-col sm:flex-row gap-2" data-testid="invite-form">
          <Input
            type="email"
            required
            placeholder="es. nonna@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-2xl border-outline flex-1"
            data-testid="invite-email-input"
          />
          <button type="submit" disabled={busy} className="btn-tactile px-6 flex items-center justify-center gap-2" data-testid="invite-send-btn">
            <Send size={16}/> {busy ? "Invio..." : "Invia invito"}
          </button>
        </form>
      </Card>

      {invites && invites.length > 0 && (
        <Card className="card-treasure p-6" data-testid="invite-list">
          <div className="text-xs uppercase tracking-widest font-bold text-ink-500 mb-3">Inviti recenti</div>
          <div className="space-y-2">
            {invites.map((inv) => {
              const ok = inv.status === "sent";
              const failed = inv.status === "failed";
              return (
                <div key={inv.invite_id} className="flex items-center gap-3 p-3 bg-parchment-dark rounded-2xl" data-testid={`invite-row-${inv.invite_id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${ok ? "bg-forest/10 text-forest" : failed ? "bg-red-100 text-compass" : "bg-gold-light text-gold-hover"}`}>
                    {ok ? <CheckCircle2 size={18}/> : failed ? <AlertCircle size={18}/> : <CircleIcon size={18}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-ink-800 truncate">{inv.email}</div>
                    <div className="text-xs text-ink-500">
                      {ok ? (inv.mocked ? "Inviato (modalità test)" : "Inviato") : failed ? "Errore invio" : "In coda"}
                      {inv.error ? ` — ${inv.error.slice(0,80)}` : ""}
                    </div>
                  </div>
                  <div className="text-xs text-ink-500">{new Date(inv.created_at).toLocaleString("it-IT", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function Family() {
  const { refresh } = useAuth();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState("");
  const [joinCode, setJoinCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/family/me");
      setFamily(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Memo: ordina i membri per punteggio decrescente. Evita di ricalcolare slice+sort ad ogni render.
  const sortedMembers = useMemo(
    () => (family?.members || []).slice().sort((a, b) => b.total_points - a.total_points),
    [family?.members]
  );

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/create", { name: createName });
      toast.success("Famiglia creata!");
      await load();
      await refresh();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Errore");
    }
  };

  const join = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family/join", { code: joinCode.toUpperCase() });
      toast.success("Sei entrato nella famiglia!");
      await load();
      await refresh();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Errore");
    }
  };

  const leave = async () => {
    if (!window.confirm("Vuoi davvero uscire dalla famiglia?")) return;
    try {
      await api.post("/family/leave");
      toast.success("Hai lasciato la famiglia");
      await load();
      await refresh();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Errore");
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(family.code);
    toast.success("Codice copiato!");
  };

  if (loading) return <div className="p-8 text-center">Caricamento...</div>;

  if (family) {
    return (
      <div className="space-y-6">
        <Card className="card-treasure overflow-hidden p-0">
          <div className="relative h-44">
            <img src={FAMILY_HERO} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="font-display font-extrabold text-3xl text-white">{family.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-white/80 text-sm">Codice:</span>
                <code className="px-3 py-1 bg-white/90 rounded-full font-display font-extrabold text-ink-900" data-testid="family-code">{family.code}</code>
                <button onClick={copyCode} className="w-9 h-9 rounded-xl bg-white/90 flex items-center justify-center" data-testid="copy-code-btn">
                  <Copy size={16} className="text-ink-800"/>
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="overview">
          <TabsList className="bg-parchment-dark rounded-2xl p-1">
            <TabsTrigger value="overview" className="rounded-xl data-[state=active]:bg-white" data-testid="family-tab-overview">Generale</TabsTrigger>
            <TabsTrigger value="invite" className="rounded-xl data-[state=active]:bg-white" data-testid="family-tab-invite">Invita</TabsTrigger>
            <TabsTrigger value="progress" className="rounded-xl data-[state=active]:bg-white" data-testid="family-tab-progress">Progressi</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <Card className="card-treasure p-6">
              <div className="text-xs uppercase tracking-widest font-bold text-ink-500 mb-3">{family.members.length} membri</div>
              <div className="space-y-2">
                {sortedMembers.map((m, i) => (
                  <div key={m.user_id} className="flex items-center gap-4 p-3 bg-parchment-dark rounded-2xl" data-testid={`family-member-${m.user_id}`}>
                    <div className="w-10 h-10 rounded-xl bg-gold flex items-center justify-center font-display font-bold text-ink-900">{i+1}</div>
                    <div className="flex-1">
                      <div className="font-bold text-ink-800">{m.name}</div>
                      <div className="text-xs text-ink-500">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-1 font-display font-extrabold text-gold-hover">
                      <Sparkles size={16}/>{m.total_points}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={leave} className="mt-6 text-sm text-compass font-bold flex items-center gap-2" data-testid="leave-family-btn">
                <LogOut size={16}/> Esci dalla famiglia
              </button>
            </Card>
          </TabsContent>
          <TabsContent value="progress" className="mt-4">
            <ProgressTab />
          </TabsContent>
          <TabsContent value="invite" className="mt-4">
            <InviteTab />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gold flex items-center justify-center shadow-tactile">
          <Users className="text-ink-900" size={26}/>
        </div>
        <div>
          <h1 className="font-display font-extrabold text-3xl text-ink-900">Area Famiglia</h1>
          <p className="text-ink-500 text-sm">Esplora insieme, vinci insieme</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="card-treasure p-6">
          <Plus className="text-gold-hover mb-3" size={28}/>
          <h2 className="font-display font-bold text-xl text-ink-900 mb-1">Crea famiglia</h2>
          <p className="text-sm text-ink-700 mb-4">Per genitori: crea il gruppo e invita i bambini.</p>
          <form onSubmit={create} className="space-y-3" data-testid="create-family-form">
            <Label>Nome famiglia</Label>
            <Input value={createName} onChange={(e)=>setCreateName(e.target.value)} placeholder="Es. Famiglia Rossi" className="h-12 rounded-2xl border-outline" data-testid="family-name-input" required/>
            <button type="submit" className="btn-tactile w-full" data-testid="create-family-btn">Crea</button>
          </form>
        </Card>

        <Card className="card-treasure p-6">
          <KeyRound className="text-ocean mb-3" size={28}/>
          <h2 className="font-display font-bold text-xl text-ink-900 mb-1">Unisciti</h2>
          <p className="text-sm text-ink-700 mb-4">Hai un codice? Inseriscilo qui per entrare.</p>
          <form onSubmit={join} className="space-y-3" data-testid="join-family-form">
            <Label>Codice invito</Label>
            <Input value={joinCode} onChange={(e)=>setJoinCode(e.target.value.toUpperCase())} placeholder="ABCDEF" className="h-12 rounded-2xl border-outline uppercase tracking-widest font-display font-bold" data-testid="family-code-input" required/>
            <button type="submit" className="btn-tactile w-full" data-testid="join-family-btn">Unisciti</button>
          </form>
        </Card>
      </div>
    </div>
  );
}
